import { del, get, post, put } from './client'
import { Giveaway, GiveawayType, PrizeType, giveawayListFrom } from '@/models/giveaway'
import { Objective } from '@/models/objective'
import { User } from '@/models/user'
import { Media, type MediaUpload, mediaListFrom } from '@/models/media'
import { GameInfo } from '@/models/game-info'
import { DiscordGuildRegister, guildListFrom } from '@/models/discord-guild'
import { UserPrize } from '@/models/prize'
import { Entrant } from '@/models/entrant'
import { type PlatformConnection, platformConnectionListFrom } from '@/models/external-platform'
import { type AccountNotification, parseAccountNotification } from '@/models/account-notification'

/**
 * One module per original `src/api/*.js` file, minus the Vuex plumbing that
 * was interleaved with them. Each function returns a hydrated model, so no
 * screen ever touches a raw API payload.
 */

/* ---------------------------------------------------------------- auth ---- */

export interface AuthResponse {
  token: string
  user: User
}

export async function oauthDiscord(code: string): Promise<AuthResponse> {
  const data = await post<{ token: string; user: unknown }>('/auth/discord', { code })
  return { token: data.token, user: new User(data.user) }
}

export async function login(username: string, password: string): Promise<AuthResponse> {
  const data = await post<{ token: string; user: unknown }>('/auth/login', { username, password })
  return { token: data.token, user: new User(data.user) }
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  nickname?: string
  profile_picture_id?: number
}

export async function register(body: RegisterRequest): Promise<AuthResponse> {
  const data = await post<{ token: string; user: unknown }>('/auth/register', body)
  return { token: data.token, user: new User(data.user) }
}

export interface UsernameAvailability {
  username: string
  available: boolean
  /** `username_taken` or `validation_failed` when not available. */
  error?: string
  /** Why it isn't available, ready to show under the field. */
  message?: string
}

/** Live check for the sign-up form; the API applies the same rules as /auth/register. */
export const checkUsernameAvailable = (username: string): Promise<UsernameAvailability> =>
  get('/auth/username/available', { params: { username } })

/* ------------------------------------------------------------------ me ---- */

export const getMyDetails = async (): Promise<User> => new User(await get('/me'))

export const updateMyDetails = async (body: Partial<User>): Promise<User> =>
  new User(await post('/me', body))

/**
 * Every key this account has won. The UserPrize model was ported for this and
 * then left unwired, so "My Loot" only ever showed the single Spawn World key
 * from /me/spawn_key -- a page about your loot that could not show your loot.
 */
export const getMyLoot = async (): Promise<UserPrize[]> => {
  const rows = await get<unknown>('/me/loot')
  return Array.isArray(rows) ? rows.map((row) => new UserPrize(row)) : []
}

export const getMyGiveaways = async (): Promise<Giveaway[]> =>
  giveawayListFrom(await get('/me/giveaways'))

export const getMyMedias = async (): Promise<Media[]> => mediaListFrom(await get('/me/medias'))

/* ------------------------------------------------- linked accounts ---- */

/** Every external platform, with whether this account has linked it. */
export const getMyPlatforms = async (): Promise<PlatformConnection[]> =>
  platformConnectionListFrom(await get('/me/external_platforms'))

/** Account notifications newer than `after` (an id), oldest first. */
export async function getMyNotifications(after: number): Promise<AccountNotification[]> {
  const rows = await get<unknown>('/me/notifications', { params: { after } })
  return Array.isArray(rows)
    ? rows.map(parseAccountNotification).filter((n): n is AccountNotification => n !== null)
    : []
}

/**
 * Attach a Discord account to the signed-in user. Unlike /auth/discord, this
 * never signs in as (or creates) another account. Returns the updated list.
 */
export const linkDiscord = async (code: string): Promise<PlatformConnection[]> =>
  platformConnectionListFrom(await post('/me/external_platforms/discord', { code }))

/* ------------------------------------------------------------- users ---- */

/**
 * A creator's public profile and the giveaways they run, addressed by username
 * (`/u/:username`).
 *
 * The Quasar app had no notion of another user's page — owner names were
 * rendered as text with a pointer cursor and no handler. These mirror the
 * shapes `/me` and `/me/giveaways` already return.
 */
export const getUser = async (username: string): Promise<User> =>
  new User(await get(`/user/${encodeURIComponent(username)}`))

export const getUserGiveaways = async (username: string): Promise<Giveaway[]> =>
  giveawayListFrom(await get(`/user/${encodeURIComponent(username)}/giveaways`))

/* ------------------------------------------------------------ giveaway ---- */

/** Accepts either a numeric id or a slug; the API resolves both. */
export const getGiveaway = async (idOrSlug: string | number): Promise<Giveaway> =>
  new Giveaway(await get(`/giveaway/${encodeURIComponent(String(idOrSlug))}`))

export const createGiveaway = async (giveaway: Giveaway): Promise<Giveaway> =>
  new Giveaway(await post('/giveaway', giveaway.payload()))

/**
 * The fields an organiser may change after a giveaway is live.
 *
 * Schedule and prize type are deliberately absent: entrants have already acted
 * on those terms, so moving the end date or swapping the prize out from under
 * them would invalidate entries that were made in good faith. Presentation —
 * what it is called, how it reads, the link it lives at — stays editable, as
 * does the key list (fixing a bad key or adding more doesn't change the deal)
 * and the entry options (removing one never takes entries away).
 */
export interface GiveawayEdit {
  id: number
  name: string
  slug: string
  description: string | null
  terms: string | null
  cover_media_id: number | null
  video_url: string | null
  gallery: string[]
  hide_entries: boolean
  /** First-come: a key needs every entry option done, not just one. */
  require_all_objectives?: boolean
  /** The full replacement key list. Sent only when the keys were changed. */
  prizes?: string[]
  /**
   * The full list of entry options wanted, sent only when they were changed.
   * Existing options are `{ id, entries_worth }`; new ones are complete. Live
   * options left out are removed — hidden, with their entries still counting.
   */
  giveaway_objective?: Record<string, unknown>[]
}

export const updateGiveaway = async (edit: GiveawayEdit): Promise<Giveaway> => {
  const { id, ...body } = edit
  return new Giveaway(await put(`/giveaway/${id}`, body))
}

export const deleteGiveaway = (id: number): Promise<unknown> => del(`/giveaway/${id}`)

/** Feature or unfeature a giveaway. Admins only. */
export const setGiveawayFeatured = async (id: number, featured: boolean): Promise<Giveaway> =>
  new Giveaway(await put(`/giveaway/${id}/featured`, { is_featured: featured }))

/** Who entered a giveaway, with emails and linked accounts. Organiser only. */
export const getGiveawayEntrants = async (id: number): Promise<Entrant[]> => {
  const rows = await get<unknown>(`/giveaway/${id}/entrants`)
  return Array.isArray(rows) ? rows.map((row) => new Entrant(row)) : []
}

/**
 * A slice of a feed. The API clamps `limit` and ignores nonsense, so these are
 * a request rather than a contract; read the returned length, not the one asked
 * for, to decide whether a page was the last.
 */
export interface FeedPage {
  limit?: number
  offset?: number
}

function feedQuery(page?: FeedPage): string {
  const params = new URLSearchParams()
  if (page?.limit !== undefined) params.set('limit', String(page.limit))
  if (page?.offset) params.set('offset', String(page.offset))
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const getNewGiveaways = async (page?: FeedPage): Promise<Giveaway[]> =>
  giveawayListFrom(await get(`/giveaway/category/new${feedQuery(page)}`))

export const getEndingGiveaways = async (page?: FeedPage): Promise<Giveaway[]> =>
  giveawayListFrom(await get(`/giveaway/category/ending${feedQuery(page)}`))

export const getFeaturedGiveaways = async (page?: FeedPage): Promise<Giveaway[]> =>
  giveawayListFrom(await get(`/giveaway/category/featured${feedQuery(page)}`))

/** Live giveaways ranked by entries taken in the last 24 hours. */
export const getTrendingGiveaways = async (page?: FeedPage): Promise<Giveaway[]> =>
  giveawayListFrom(await get(`/giveaway/category/trending${feedQuery(page)}`))

/** Live giveaways ranked by entries overall. */
export const getPopularGiveaways = async (page?: FeedPage): Promise<Giveaway[]> =>
  giveawayListFrom(await get(`/giveaway/category/popular${feedQuery(page)}`))

export interface EnterGiveawayArgs {
  giveaway_id: number
  giveaway_objective_id: number
  /** Whose referral link brought them here; credited if this is their first entry. */
  referrer_id?: number | null
}

export const enterGiveaway = ({
  giveaway_id,
  giveaway_objective_id,
  referrer_id,
}: EnterGiveawayArgs): Promise<unknown> =>
  post(`/giveaway/${giveaway_id}/enter`, {
    giveaway_objective_id,
    ...(referrer_id ? { referrer_id } : {}),
  })

/* --------------------------------------------------------------- enums ---- */

export const getGiveawayTypes = async (): Promise<GiveawayType[]> => {
  const data = await get<unknown[]>('/enums/giveaway_types')
  return (data ?? []).map((item) => new GiveawayType(item))
}

export const getPrizeTypes = async (): Promise<PrizeType[]> => {
  const data = await get<unknown[]>('/enums/prize_types')
  return (data ?? []).map((item) => new PrizeType(item))
}

export const getObjectives = async (): Promise<Objective[]> => {
  const data = await get<unknown[]>('/enums/objectives')
  return (data ?? []).map((item) => new Objective(item))
}

/* ----------------------------------------------------------- game_info ---- */

export const searchGameInfo = async (query: string): Promise<GameInfo[]> => {
  const data = await post<unknown[]>('/game_info/search', { query })
  return (data ?? []).map((item) => new GameInfo(item))
}

export const createGameInfo = async (body: GameInfo): Promise<GameInfo> =>
  new GameInfo(await post('/game_info', body))

/** Custom games the signed-in user has added. */
export const getMyGames = async (): Promise<GameInfo[]> => {
  const rows = await get<unknown>('/me/game_info')
  return Array.isArray(rows) ? rows.map((row) => new GameInfo(row)) : []
}

/** Edit a custom game. Only its owner may; the API answers 403 otherwise. */
export const updateGameInfo = async (id: number, body: GameInfo): Promise<GameInfo> =>
  new GameInfo(await put(`/game_info/${id}`, body))

export const autocompleteGameInfo = async (query: string): Promise<GameInfo[]> => {
  const data = await post<unknown[]>('/search/game_info/autocomplete', { query })
  return (data ?? []).map((item) => new GameInfo(item))
}

/* --------------------------------------------------------------- media ---- */

export const uploadMedia = async (media: MediaUpload): Promise<Media> => {
  if (!media.file) throw new Error('No file selected')
  const form = new FormData()
  form.append('image_file', media.file)
  form.append('name', media.name ?? media.file.name)
  // The API takes tags as a repeated field; the original sent the array
  // stringified, which produced a single "a,b,c" tag.
  for (const tag of media.tags ?? []) form.append('tags', tag)
  return new Media(await post('/media', form))
}

/**
 * Delete an image you uploaded. The API refuses (409, with a message naming
 * where) while a giveaway, game or profile still shows it.
 */
/** Rename or retag an image you uploaded. */
export const updateMedia = async (
  id: number,
  body: { name: string; tags: string[] },
): Promise<Media> => new Media(await put(`/media/${id}`, body))

export const deleteMedia = (id: number): Promise<unknown> => del(`/media/${id}`)

/* ------------------------------------------------------------- discord ---- */

export const getManagedGuilds = async (): Promise<DiscordGuildRegister[]> =>
  guildListFrom(await get('/discord/guilds/managed'))

export const registerGuild = async (body: {
  guild_id: string
  permissions: string
}): Promise<DiscordGuildRegister> => new DiscordGuildRegister(await post('/discord/guilds', body))
